import React, { useState } from 'react';
import { Button, ButtonGroup, Drawer, Form } from '@common/components';
import { useMutation } from '@common/hooks';
import {
  createOrganizationBusinessMutation,
  CreateOrganizationBusinessMutationVariables,
  CreateOrganizationBusinessMutationResponse,
} from '@institutions/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  buildOrganizationBusiness,
  OrganizationFacilityForm,
  FacilityFormValues,
} from '@institutions/components/organization-facility-form';

const errorToast = () =>
  createMessage('error', 'Something went wrong. Try again later.');

export function OrganizationFacilityAddDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [form] = Form.useForm();
  const [isSubmitButtonEnabled, setIsSubmitButtonEnabled] = useState(false);
  const [save, { loading: mutationLoading }] = useMutation<
    CreateOrganizationBusinessMutationResponse,
    CreateOrganizationBusinessMutationVariables
  >(createOrganizationBusinessMutation, {
    refetchQueries: ['FacilitiesAndUnitsQuery'],
  });

  function handleAlertOnSuccess() {
    setIsSubmitButtonEnabled(false);
    onClose();
    createMessage('success', 'Facility successfully saved.');
  }

  async function handleSave(payload: unknown) {
    try {
      const response = await save({
        variables: {
          input: buildOrganizationBusiness(payload as FacilityFormValues),
        },
      });

      if (response?.errors?.length) {
        const messages = response?.errors?.map(({ message }) => message);
        createMessage('error', messages?.join(', '));
      } else {
        handleAlertOnSuccess();
      }
    } catch {
      errorToast();
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  function handleSubmitButtonEnabled(values) {
    const allFieldsFilled = Object.values(values).every(Boolean);

    if (allFieldsFilled) {
      setIsSubmitButtonEnabled(true);
    } else {
      setIsSubmitButtonEnabled(false);
    }
  }

  const footer = (
    <ButtonGroup>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        kind="primary"
        onClick={form.submit}
        loading={mutationLoading}
        disabled={!isSubmitButtonEnabled}
      >
        Save
      </Button>
    </ButtonGroup>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className="organization-facility-edit-drawer"
      title="New Facility Details"
      footer={footer}
    >
      <OrganizationFacilityForm
        form={form}
        onFinish={handleSave}
        onValuesChange={(_, values) => handleSubmitButtonEnabled(values)}
        initialValues={{ country: 'US' }}
      />
    </Drawer>
  );
}
